import React, {useMemo} from 'react';
import {RouteInfo} from 'sfccAPI/interfaces';

import {useLocalesMgr, useLocalizeContent, useLocation, useSSRRedirect, useSSRResponse, useSuspenseCmsContent} from 'coreHooks';
import {Redirect} from 'coreUtils/adapters/router';
import {getContentPageCmsQuery} from 'coreUtils/cms/contentPageCmsQuery';
import {getConfig, isServer} from 'coreUtils/utils';

import RouteManager from '../RouteManager';

interface Props {
	routeInfo: RouteInfo
	slug: string
	isHomePage: boolean
	ComponentToLoad: React.ReactNode
}

export default (
	{
		routeInfo,
		slug,
		isHomePage,
		ComponentToLoad: LocalComponentToLoad
	}: Props
) => {
	const res = useSSRResponse();
	const localesMgr = useLocalesMgr();
	const defaultLocale = localesMgr.getDefaultLocale().getLanguageCode();
	const currentLocale = localesMgr.getCurrentLanguageCode(true);
	const location = useLocation();
	const appConfig = getConfig();
	const redirect = useSSRRedirect();
	let slugLocale = currentLocale;

	try {
		slugLocale = localesMgr.parseLocaleFromURL('', location.pathname).getLanguageCode() || currentLocale;
	} catch {} // eslint-disable-line no-empty

	// Same query as the SSR prefetch and seed (`bootstrapSSR`): on the server it is read from the cache.
	const {data} = useSuspenseCmsContent(getContentPageCmsQuery({
		slug,
		slugLocale,
		defaultLocale,
		multiSitePages: !!appConfig?.app?.cmsAPI?.multiSiteEnabled?.pages,
	}));

	const localizedContent = useLocalizeContent(data);

	if (isServer && res?.cacheTagManager) {
		res.cacheTagManager.addContentfulTag();
		localizedContent?.pageCollection?.items?.forEach((c) => res.cacheTagManager.addContentfulTag(c.sys.id));
	}

	const ComponentToLoad = useMemo(() => {
		return data?.pageCollection?.items?.[0] ? RouteManager.getRoute('Content-Show').component : LocalComponentToLoad;
	}, [data]);

	if (localizedContent?.pageCollection?.items?.[0]?.slug && localizedContent?.pageCollection?.items?.[0]?.slug !== slug) {
		const redirectUrl = localesMgr.updateLinkWithLocale(localizedContent?.pageCollection?.items?.[0]?.slug);

		if (isServer) {
			redirect(301, redirectUrl);
		}

		return <Redirect to={redirectUrl} />;
	}

	return (
		<ComponentToLoad
			localizedContent={localizedContent?.pageCollection?.items?.[0]}
			content={data?.pageCollection?.items?.[0]}
			isHomePage={isHomePage}
			routeInfo={routeInfo}
			slug={slug}
		/>
	);
};
